import React from 'react';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import ManagedDisclaimer from './ManagedDisclaimer';

// Rating cell with colour coding
const RatingCell = ({ rating, isOverall = false }) => {
  if (rating === null || rating === undefined) {
    return <td className="rating-cell rating-na">N/A</td>;
  }
  const ratingClass = `rating-${rating}`;
  const overallClass = isOverall ? 'rating-overall' : '';
  return (
    <td className={`rating-cell ${ratingClass} ${overallClass}`}>
      {rating}
    </td>
  );
};

// Format date for display
const formatReportDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr;
};

// Format date with end of month day (e.g., "January 2026" -> "31 January 2026")
const formatPerformanceDateWithDay = (dateStr) => {
  if (!dateStr) return '';
  try {
    const months = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
      'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
    };
    const parts = dateStr.trim().split(' ');
    if (parts.length !== 2) return dateStr;

    const monthName = parts[0].toLowerCase();
    const year = parseInt(parts[1], 10);
    const monthIndex = months[monthName];

    if (monthIndex === undefined || isNaN(year)) return dateStr;

    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    return `${lastDay} ${parts[0]} ${year}`;
  } catch {
    return dateStr;
  }
};

// Format date to "MMM-YYYY" for chart axes
const formatChartDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]}-${date.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

// Parse a performance-series date string ("MMM-YYYY" or ISO "YYYY-MM-DD") to a Date
const parsePerfDate = (dateStr) => {
  if (!dateStr) return null;
  const monthMap = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
  };
  const parts = String(dateStr).split('-');
  if (parts.length === 2 && monthMap[parts[0]] !== undefined) {
    return new Date(parseInt(parts[1], 10), monthMap[parts[0]], 1);
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

// Build the "Performance Growth – Since Inception" series.
// Mirrors the PDF template (report_template.html): the benchmark is trimmed to the
// fund's inception date and rebased to 0% there, then aligned to the fund's monthly
// dates so both series plot against the same x-axis. The fund series is left as-is.
const buildPerformanceSeries = (performanceData) => {
  if (!performanceData || !performanceData.fund || performanceData.fund.length === 0) {
    return null;
  }

  const fund = performanceData.fund
    .map(d => ({ ...d, _t: parsePerfDate(d.date) }))
    .filter(d => d._t)
    .sort((a, b) => a._t - b._t);
  if (fund.length === 0) return null;

  const inception = fund[0]._t;

  // Trim benchmark to fund inception and rebase to 0% at the first remaining point
  const benchmark = (performanceData.benchmark || [])
    .map(d => ({ ...d, _t: parsePerfDate(d.date) }))
    .filter(d => d._t && d._t >= inception)
    .sort((a, b) => a._t - b._t);
  const base = benchmark.length > 0 ? benchmark[0].value : 0;

  // Align benchmark to the fund's months so positions match the x-axis labels
  const monthKey = (t) => `${t.getFullYear()}-${t.getMonth()}`;
  const benchByMonth = new Map(benchmark.map(d => [monthKey(d._t), d.value - base]));

  return {
    labels: fund.map(d => formatChartDate(d.date)),
    fundValues: fund.map(d => d.value * 100),
    benchmarkValues: fund.map(d => {
      const v = benchByMonth.get(monthKey(d._t));
      return v === undefined ? null : v * 100;
    }),
  };
};

// Render a table row only if value is not N/A or null
const renderTableRow = (label, value) => {
  if (!value || value === 'N/A') return null;
  return (
    <tr key={label}>
      <td>{label}</td>
      <td>{value}</td>
    </tr>
  );
};

const ResearchReportContent = ({ reportData, displayFundName, theme, brandDomain }) => {
  if (!reportData) return null;

  // Aligned/trimmed/rebased performance-growth series (benchmark starts at fund inception)
  const performanceSeries = buildPerformanceSeries(reportData.performance_data);

  return (
    <div className="report-pages">
      {/* ========== PAGE 1 ========== */}
      <div className="report-page page-1">
        {/* Header */}
        <header className="report-header">
          <div className="header-content">
            <div className="logo-section">
              {/* Manager research is locked to Drummond branding for every client —
                  always the Drummond logo, never the practice's white-label logo. */}
              <img src="/logo-white.png" alt="Drummond Capital Partners" className="logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <div className="title-section">
              <h1 className="main-title">Drummond Manager Research</h1>
              <h2 className="fund-name">{displayFundName}</h2>
            </div>
          </div>
        </header>

        {/* Main Content - Two Column Layout */}
        <div className="main-content">
          {/* Left Column - 75% */}
          <div className="left-column">
            {/* Overview Section */}
            <section className="overview-section">
              <h3>OVERVIEW</h3>
              <div className="overview-content">
                {reportData.fund_content?.overview_text && (
                  reportData.fund_content.overview_text.split('\n').filter(p => p.trim()).map((para, idx) => (
                    <p key={idx}>{para.trim()}</p>
                  ))
                )}
              </div>
            </section>

            {/* Drummond's Opinion Section */}
            <section className="opinion-section">
              <h3>DRUMMOND'S OPINION</h3>
              <ul className="opinion-list">
                {reportData.fund_content?.opinion_points && reportData.fund_content.opinion_points.length > 0 && (
                  reportData.fund_content.opinion_points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))
                )}
              </ul>
            </section>

            {/* Portfolio Construction Considerations */}
            <section className="portfolio-section">
              <h3>PORTFOLIO CONSTRUCTION CONSIDERATIONS</h3>
              <ul className="portfolio-list">
                {reportData.fund_content?.portfolio_points && reportData.fund_content.portfolio_points.length > 0 && (
                  reportData.fund_content.portfolio_points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))
                )}
              </ul>
            </section>
          </div>

          {/* Right Column - 25% Grey Sidebar */}
          <div className="right-column">
            <div className="right-column-container">
              {/* Fund Summary */}
              <div className="fund-summary-box">
                <h4>FUND SUMMARY</h4>
                {reportData.fund_content?.fund_summary && (
                  <p>{reportData.fund_content.fund_summary}</p>
                )}
              </div>

              {/* Fund Facts */}
              <div className="fund-facts-box">
                <h4>FUND FACTS</h4>
                <table className="fund-facts-table">
                  <tbody>
                    {renderTableRow('APIR', reportData.fund_details?.apir_code)}
                    {renderTableRow('Fund Inception', reportData.fund_details?.fund_inception)}
                    {renderTableRow('Asset Class', reportData.fund_details?.asset_class)}
                    {renderTableRow('Style', reportData.fund_details?.investment_style)}
                    {renderTableRow('Currency Exposure', reportData.fund_details?.currency_exposure)}
                    {renderTableRow('Benchmark', reportData.fund_details?.benchmark)}
                    {renderTableRow('Objective', reportData.fund_details?.objectives)}
                    {renderTableRow('Distributions', reportData.fund_details?.distributions)}
                    {renderTableRow('Strategy FUM', reportData.fund_details?.strategy_fum)}
                    {renderTableRow('Capacity', reportData.fund_details?.capacity)}
                    {renderTableRow('Other', reportData.fund_details?.other)}
                  </tbody>
                </table>
              </div>

              {/* Fund Characteristics */}
              <div className="fund-data-box">
                <h4>FUND CHARACTERISTICS</h4>
                <table className="fund-data-table">
                  <tbody>
                    {renderTableRow('No. Holdings', reportData.fund_details?.holdings_count)}
                    {renderTableRow('Position Size Limit', reportData.fund_details?.position_size)}
                    {renderTableRow('Sector Limit', reportData.fund_details?.sector_limit)}
                    {renderTableRow('Country Limit', reportData.fund_details?.country_limit)}
                    {renderTableRow('Tracking Error', reportData.fund_details?.tracking_error)}
                    {renderTableRow('Turnover', reportData.fund_details?.turnover)}
                    {renderTableRow('Duration Limit', reportData.fund_details?.duration_limit)}
                    {renderTableRow('Credit Rating Limit', reportData.fund_details?.credit_rating_limit)}
                    {renderTableRow('Sub-IG Limit', reportData.fund_details?.sub_ig_limit)}
                    {renderTableRow('Derivatives', reportData.fund_details?.derivatives)}
                    {renderTableRow('Global Exposure', reportData.fund_details?.global_exposure)}
                    {renderTableRow('Pricing Frequency', reportData.fund_details?.pricing_frequency)}
                    {renderTableRow('Applications', reportData.fund_details?.applications)}
                    {renderTableRow('Redemptions', reportData.fund_details?.redemptions)}
                    {renderTableRow('Leverage', reportData.fund_details?.leverage)}
                  </tbody>
                </table>
              </div>

              {/* Fund Statistics */}
              <div className="fund-stats-box">
                <h4>FUND STATISTICS (3YR)</h4>
                {(reportData.fund_details?.annualised_return && reportData.fund_details?.annualised_return !== 'N/A') ||
                 (reportData.fund_details?.annualised_volatility && reportData.fund_details?.annualised_volatility !== 'N/A') ||
                 (reportData.fund_details?.information_ratio && reportData.fund_details?.information_ratio !== 'N/A') ? (
                  <table className="fund-stats-table">
                    <tbody>
                      {renderTableRow('Annualised Return', reportData.fund_details?.annualised_return)}
                      {renderTableRow('Annualised Volatility', reportData.fund_details?.annualised_volatility)}
                      {renderTableRow('Information Ratio', reportData.fund_details?.information_ratio)}
                    </tbody>
                  </table>
                ) : (
                  <p className="insufficient-data">Insufficient data</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page 1 Footer */}
        <div className="page-footer">
          <span className="page-number">DRUMMOND RESEARCH REPORT</span>
          <span className="page-right">PAGE 1</span>
        </div>
      </div>

      {/* ========== PAGE 2 ========== */}
      <div className="report-page page-2">
        {/* Page 2 Header */}
        <div className="page2-header">
          <h3>DRUMMOND MANAGER RESEARCH</h3>
        </div>

        <div className="page2-content">
          {/* Investment Rating Table */}
          <section className="rating-section">
            <h4>DRUMMOND INVESTMENT RATING</h4>
            <table className="rating-table">
              <thead>
                <tr>
                  <th>People</th>
                  <th>Process</th>
                  <th>ESG Integration</th>
                  <th>Performance</th>
                  <th>Business Model</th>
                  <th>Fees &amp; Terms</th>
                  <th className="overall">Overall</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <RatingCell rating={reportData.investment_rating?.people} />
                  <RatingCell rating={reportData.investment_rating?.process} />
                  <RatingCell rating={reportData.investment_rating?.esg} />
                  <RatingCell rating={reportData.investment_rating?.performance} />
                  <RatingCell rating={reportData.investment_rating?.business} />
                  <RatingCell rating={reportData.investment_rating?.fees} />
                  <RatingCell rating={reportData.investment_rating?.overall} isOverall={true} />
                </tr>
              </tbody>
            </table>
          </section>

          {/* Performance Table */}
          {reportData.performance_table && (
            <section className="performance-section">
              <h4>FUND PERFORMANCE (NET) – {formatPerformanceDateWithDay(reportData.performance_date || reportData.report_date)}</h4>
              <table className="performance-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>1m</th>
                    <th>3m</th>
                    <th>6m</th>
                    <th>1yr</th>
                    <th>2yr pa</th>
                    <th>3yr pa</th>
                    <th>5yr pa</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{reportData.performance_table.fund?.name}{reportData.performance_table.fund?.apir_code ? ` (${reportData.performance_table.fund.apir_code})` : ''}</td>
                    <td>{reportData.performance_table.fund?.perf_1m}</td>
                    <td>{reportData.performance_table.fund?.perf_3m}</td>
                    <td>{reportData.performance_table.fund?.perf_6m}</td>
                    <td>{reportData.performance_table.fund?.perf_1yr}</td>
                    <td>{reportData.performance_table.fund?.perf_2yr}</td>
                    <td>{reportData.performance_table.fund?.perf_3yr}</td>
                    <td>{reportData.performance_table.fund?.perf_5yr}</td>
                  </tr>
                  <tr>
                    <td>{reportData.performance_table.benchmark?.name}{reportData.performance_table.benchmark?.apir_code ? ` (${reportData.performance_table.benchmark.apir_code})` : ''}</td>
                    <td>{reportData.performance_table.benchmark?.perf_1m}</td>
                    <td>{reportData.performance_table.benchmark?.perf_3m}</td>
                    <td>{reportData.performance_table.benchmark?.perf_6m}</td>
                    <td>{reportData.performance_table.benchmark?.perf_1yr}</td>
                    <td>{reportData.performance_table.benchmark?.perf_2yr}</td>
                    <td>{reportData.performance_table.benchmark?.perf_3yr}</td>
                    <td>{reportData.performance_table.benchmark?.perf_5yr}</td>
                  </tr>
                  <tr className="alpha-row">
                    <td>Excess Return</td>
                    <td>{reportData.performance_table.alpha?.perf_1m}</td>
                    <td>{reportData.performance_table.alpha?.perf_3m}</td>
                    <td>{reportData.performance_table.alpha?.perf_6m}</td>
                    <td>{reportData.performance_table.alpha?.perf_1yr}</td>
                    <td>{reportData.performance_table.alpha?.perf_2yr}</td>
                    <td>{reportData.performance_table.alpha?.perf_3yr}</td>
                    <td>{reportData.performance_table.alpha?.perf_5yr}</td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: '0.75rem', fontStyle: 'italic', color: '#6c757d', margin: '4px 0 0 0' }}>Benchmark is a passive comparator selected by Drummond and is not the fund&rsquo;s official benchmark.</p>
            </section>
          )}

          {/* Charts Section */}
          <div className="charts-container">
            {/* Performance Growth Chart - Full Width */}
            <div className="chart-box chart-large">
              <h5>PERFORMANCE GROWTH – {reportData.performance_rebased ? 'SINCE INCEPTION' : '3 YEARS'}</h5>
              <div className="chart-placeholder">
                {performanceSeries ? (
                  <Line
                    data={{
                      labels: performanceSeries.labels,
                      datasets: [
                        {
                          label: reportData.performance_data.fund_name,
                          data: performanceSeries.fundValues,
                          borderColor: '#337B91',
                          backgroundColor: 'transparent',
                          borderWidth: 2,
                          tension: 0.1,
                          fill: false,
                          pointRadius: 0,
                        },
                        {
                          label: reportData.performance_data.benchmark_name,
                          data: performanceSeries.benchmarkValues,
                          borderColor: '#A6A6A6',
                          backgroundColor: 'transparent',
                          borderWidth: 2,
                          tension: 0.1,
                          fill: false,
                          pointRadius: 0,
                          spanGaps: true,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false,
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          ticks: { maxTicksLimit: 8 }
                        },
                        y: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          ticks: { callback: (value) => value.toFixed(0) + '%' }
                        }
                      }
                    }}
                  />
                ) : (
                  <p className="insufficient-data">Chart data unavailable</p>
                )}
              </div>
              {/* Legend for Performance Growth Chart */}
              <div className="chart-legend">
                <div className="legend-item">
                  <div className="legend-line" style={{ backgroundColor: '#337B91' }}></div>
                  <span>{displayFundName || reportData.performance_data?.fund_name || reportData.fund_details?.fund_name}</span>
                </div>
                <div className="legend-item">
                  <div className="legend-line" style={{ backgroundColor: '#A6A6A6' }}></div>
                  <span>{reportData.performance_data?.benchmark_name || 'Benchmark'}</span>
                </div>
              </div>
            </div>

            {/* Row 2: Drawdown + Risk Reward */}
            <div className="chart-box chart-medium">
              <h5>FUND DRAWDOWN ANALYSIS</h5>
              <div className="chart-placeholder">
                {reportData.drawdown_data && reportData.drawdown_data.fund && reportData.drawdown_data.fund.length > 0 ? (
                  <Line
                    data={{
                      labels: reportData.drawdown_data.fund.map(d => formatChartDate(d.date)),
                      datasets: [
                        {
                          label: reportData.drawdown_data.fund_name,
                          data: reportData.drawdown_data.fund.map(d => d.value),
                          borderColor: '#337B91',
                          backgroundColor: 'rgba(51, 123, 145, 0.1)',
                          borderWidth: 2,
                          tension: 0.1,
                          fill: true,
                          pointRadius: 0,
                        },
                        {
                          label: reportData.drawdown_data.benchmark_name,
                          data: reportData.drawdown_data.benchmark.map(d => d.value),
                          borderColor: '#A6A6A6',
                          backgroundColor: 'rgba(166, 166, 166, 0.1)',
                          borderWidth: 2,
                          tension: 0.1,
                          fill: true,
                          pointRadius: 0,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false,
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          ticks: { maxTicksLimit: 6 }
                        },
                        y: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          max: 0,
                          ticks: { callback: (value) => value.toFixed(0) + '%' }
                        }
                      }
                    }}
                  />
                ) : (
                  <p className="insufficient-data">Chart data unavailable</p>
                )}
              </div>
            </div>

            <div className="chart-box chart-medium">
              <h5>RISK REWARD COMPARISON – 3 YEARS</h5>
              <div className="chart-placeholder">
                {reportData.risk_reward && reportData.risk_reward.fund && reportData.risk_reward.benchmark &&
                 reportData.risk_reward.fund.risk != null && reportData.risk_reward.fund.return != null &&
                 reportData.risk_reward.benchmark.risk != null && reportData.risk_reward.benchmark.return != null ? (
                  <Scatter
                    data={{
                      datasets: [
                        {
                          label: reportData.risk_reward.fund.name,
                          data: [{ x: reportData.risk_reward.fund.risk, y: reportData.risk_reward.fund.return }],
                          backgroundColor: '#337B91',
                          pointRadius: 8,
                        },
                        {
                          label: reportData.risk_reward.benchmark.name,
                          data: [{ x: reportData.risk_reward.benchmark.risk, y: reportData.risk_reward.benchmark.return }],
                          backgroundColor: '#A6A6A6',
                          pointRadius: 8,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: {
                          title: { display: true, text: 'Risk (Std Dev %)' },
                          grid: { color: 'rgba(0,0,0,0.1)' },
                          border: { color: '#072040' },
                          min: 0,
                          ticks: { callback: (value) => value + '%' }
                        },
                        y: {
                          title: { display: true, text: 'Return %' },
                          grid: { color: 'rgba(0,0,0,0.1)' },
                          border: { color: '#072040' },
                          min: 0,
                          ticks: { callback: (value) => value + '%' }
                        }
                      }
                    }}
                  />
                ) : (
                  <p className="insufficient-data">Insufficient data</p>
                )}
              </div>
            </div>

            {/* Row 3: Calendar Returns + Rolling Alpha */}
            <div className="chart-box chart-medium">
              <h5>FUND CALENDAR YEAR RETURNS</h5>
              <div className="chart-placeholder">
                {reportData.has_sufficient_calendar_data && reportData.calendar_returns && reportData.calendar_returns.fund && reportData.calendar_returns.fund.length > 0 ? (
                  <Bar
                    data={{
                      labels: reportData.calendar_returns.fund.map(d => d.year),
                      datasets: [
                        {
                          label: reportData.calendar_returns.fund_name,
                          data: reportData.calendar_returns.fund.map(d => d.value),
                          backgroundColor: '#337B91',
                        },
                        {
                          label: reportData.calendar_returns.benchmark_name,
                          data: reportData.calendar_returns.benchmark.map(d => d.value),
                          backgroundColor: '#A6A6A6',
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: {
                          grid: { display: false },
                          border: { color: '#072040' }
                        },
                        y: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          ticks: { callback: (value) => value + '%' }
                        }
                      }
                    }}
                  />
                ) : (
                  <p className="insufficient-data">Insufficient data</p>
                )}
              </div>
            </div>

            <div className="chart-box chart-medium">
              <h5>FUND ROLLING ALPHA – 1 YEAR</h5>
              <div className="chart-placeholder">
                {reportData.has_sufficient_alpha_data && reportData.rolling_alpha && reportData.rolling_alpha.data && reportData.rolling_alpha.data.length > 0 ? (
                  <Line
                    data={{
                      labels: reportData.rolling_alpha.data.map(d => formatChartDate(d.date)),
                      datasets: [
                        {
                          label: 'Rolling 1Yr Alpha',
                          data: reportData.rolling_alpha.data.map(d => d.value),
                          borderColor: '#337B91',
                          backgroundColor: 'transparent',
                          borderWidth: 2,
                          tension: 0.1,
                          fill: false,
                          pointRadius: 0,
                          pointHoverRadius: 5,
                          pointHoverBackgroundColor: '#337B91',
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false,
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            label: (context) => `Alpha: ${context.parsed.y.toFixed(2)}%`
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          ticks: { maxTicksLimit: 6 }
                        },
                        y: {
                          grid: { display: false },
                          border: { color: '#072040' },
                          ticks: { callback: (value) => value.toFixed(1) + '%' }
                        }
                      }
                    }}
                  />
                ) : (
                  <p className="insufficient-data">Insufficient data</p>
                )}
              </div>
            </div>
          </div>

          {/* Chart Source */}
          <p className="chart-source">Source: LSEG Workspace, Drummond Capital Partners.</p>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer-section">
          <h5>DISCLAIMER</h5>
          {/* Same Admin > Disclaimers text (fund_research_report) and brand rule as the PDF */}
          <ManagedDisclaimer dkey="fund_research_report" className="research-disclaimer-text" brandDomain={brandDomain} />
        </div>

        {/* Page 2 Footer */}
        <div className="page-footer">
          <span className="page-number">DRUMMOND RESEARCH REPORT</span>
          <span className="page-right">PAGE 2</span>
        </div>
      </div>
    </div>
  );
};

export { RatingCell, formatReportDate, formatPerformanceDateWithDay, formatChartDate, renderTableRow };
export default ResearchReportContent;
